import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import PaymentModal from '../../components/ui/PaymentModal';
import Icon from '../../components/AppIcon';

import ExecutiveSummaryTab from './components/ExecutiveSummaryTab';
import HistoricalAnalysisTab from './components/HistoricalAnalysisTab';
import PredictiveAccuracyTab from './components/PredictiveAccuracyTab';
import ModuleIntegrationTab from './components/ModuleIntegrationTab';
import ExportPanel from './components/ExportPanel';

const ReportsDashboard = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('executive');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState('6months');
  const [bmcReportData, setBmcReportData] = useState([]);
  const [bmcReportsHistory, setBmcReportsHistory] = useState([]);

  useEffect(() => {
    const savedReport = localStorage.getItem('bmc_report');
    const savedHistory = localStorage.getItem('bmc_reports_history');

    if (savedReport) {
      try {
        const parsedData = JSON.parse(savedReport);
        setBmcReportData(Array.isArray(parsedData) ? parsedData : []);
      } catch (error) {
        console.error('Failed to parse bmc_report from localStorage:', error);
        setBmcReportData([]);
      }
    }

    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setBmcReportsHistory(Array.isArray(parsedHistory) ? parsedHistory : []);
      } catch (error) {
        console.error('Failed to parse bmc_reports_history from localStorage:', error);
        setBmcReportsHistory([]);
      }
    }
  }, []);


  const tabs = [
    { id: 'executive', label: 'Executive Summary', icon: 'BarChart3' },
    { id: 'historical', label: 'Historical Analysis', icon: 'TrendingUp' },
    { id: 'predictive', label: 'Predictive Accuracy', icon: 'Target' },
    { id: 'integration', label: 'Module Integration', icon: 'GitMerge' }
  ];

  const handleExport = () => {
    setIsPaymentModalOpen(true);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'executive':
        return <ExecutiveSummaryTab dateRange={dateRange} />;
      case 'historical':
        return <HistoricalAnalysisTab dateRange={dateRange} />;
      case 'predictive':
        return <PredictiveAccuracyTab dateRange={dateRange} />;
      case 'integration':
        return <ModuleIntegrationTab dateRange={dateRange} />;
      default:
        return <ExecutiveSummaryTab dateRange={dateRange} />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AuthHeader
        onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        isSidebarCollapsed={isSidebarCollapsed}
      />

      <Sidebar isCollapsed={isSidebarCollapsed} userSubscription="free" />

      <main
        className={`pt-20 transition-smooth ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-60'
          }`}
      >
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 space-y-4 md:space-y-6">
          {/* Header Section */}
          <div className="flex flex-col gap-3 md:gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground font-heading">
                Business Intelligence Reports
              </h1>
              <p className="text-sm md:text-base text-muted-foreground font-caption mt-1">
                Comprehensive performance summaries and analytical insights from all modules
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {['3months', '6months', 'year'].map((period) => (
                <button
                  key={period}
                  onClick={() => setDateRange(period)}
                  className={`px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium font-caption transition-smooth ${dateRange === period
                      ? 'bg-primary text-primary-foreground elevation-sm'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                >
                  {period === '3months' ? '3M' : period === '6months' ? '6M' : '1Y'}
                </button>
              ))}
            </div>
          </div>

          {/* Business Model Canvas Report Section */}
          <div className="bg-card border border-border rounded-xl p-4 md:p-6 elevation-sm">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Icon name="FileText" size={20} color="var(--color-primary)" />
              </div>

              <div className="min-w-0">
                <h2 className="text-xl md:text-2xl font-bold text-foreground font-heading">
                  Business Model Canvas Report
                </h2>
                <p className="text-sm text-muted-foreground font-caption mt-1">
                  Generated from your completed Business Model Canvas
                </p>
              </div>
            </div>

            {bmcReportData.length === 0 ? (
              <div className="border border-dashed border-border rounded-lg p-6 text-center bg-muted/20">
                <p className="text-sm text-muted-foreground">
                  No Business Model Canvas report data found.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Go to the Business Model Canvas page and click Generate Report.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {bmcReportData.map((block) => (
                  <div
                    key={block.id}
                    className="border border-border rounded-lg p-4 md:p-5 bg-muted/30 overflow-hidden"
                  >
                    <h3 className="text-lg font-semibold text-foreground mb-1 break-words">
                      {block.title}
                    </h3>

                    <p className="text-sm text-muted-foreground mb-3 break-words">
                      {block.description}
                    </p>

                    <div className="whitespace-pre-line break-words text-sm md:text-base text-foreground leading-7 max-h-[320px] overflow-y-auto pr-1">
                      {block.value || 'No data provided'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>


          <div className="bg-card border border-border rounded-xl p-4 md:p-6 elevation-sm">
            <h2 className="text-xl md:text-2xl font-bold text-foreground font-heading mb-2">
              Generated Reports History
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Previously generated Business Model Canvas reports
            </p>

            {bmcReportsHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground">No previous reports found.</p>
            ) : (
              <div className="space-y-4">
                {bmcReportsHistory.map((report) => (
                  <div
                    key={report.id}
                    className="border border-border rounded-lg p-4 bg-muted/30"
                  >
                    <h3 className="text-base font-semibold text-foreground mb-1">
                      Report - {report.createdAt}
                    </h3>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4">
                      {report.data?.map((block) => (
                        <div
                          key={`${report.id}-${block.id}`}
                          className="border border-border rounded-lg p-4 bg-background"
                        >
                          <h4 className="text-lg font-semibold text-foreground mb-1">
                            {block.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mb-2">
                            {block.description}
                          </p>
                          <div className="whitespace-pre-line break-words text-sm md:text-base text-foreground leading-7 max-h-[220px] overflow-y-auto pr-1">
                            {block.value || 'No data provided'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>



          {/* Tabs Navigation */}
          <div className="bg-card border border-border rounded-lg p-1.5 md:p-2 elevation-sm overflow-x-auto">
            <div className="flex gap-1 md:gap-2 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-2.5 rounded-md text-xs md:text-sm font-medium font-body transition-smooth whitespace-nowrap ${activeTab === tab.id
                      ? 'bg-primary text-primary-foreground elevation-sm'
                      : 'text-muted-foreground hover:bg-muted/50'
                    }`}
                >
                  <Icon name={tab.icon} size={14} className="md:w-4 md:h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-4 md:space-y-6">
            {renderTabContent()}
          </div>

          {/* Export Panel */}
          <ExportPanel onExport={handleExport} />
        </div>
      </main>

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        featureName="Premium Reports Export"
        featureDescription="Download comprehensive PDF and Excel reports with advanced analytics"
        onSuccess={() => {
          console.log('Payment successful');
        }}
      />
    </div>
  );
};

export default ReportsDashboard;